import { CategorizedWells, useFetchAllCategorizedWells } from 'hooks/useFetchAllCategorizedWells';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { WellFullDetailsWithUniqueKeyDTO } from 'types/well';

import { wellNameType } from '../AdvanceSearch/AdvanceSearch';

interface Props {
  open: boolean;
  onClose: () => void;
}

const MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH = 2;
const SELECT_ALL = 'Select All';
const UNSELECT_ALL = 'Unselect All';

export const useFavouritePopin = ({ open, onClose }: Props) => {
  const [selectedCountries, setSelectedContries] = useState<string[]>([]);
  const [debouncedSearchInput, setSearchInput] = useState('');
  const [selectedCategorisedWells, setSelectedCategorisedWells] = useState<CategorizedWells>(
    {} as CategorizedWells,
  );
  const [endPage, setEndPage] = useState(20);
  const [isShowAdvancedSearch, setAdvancedSearch] = useState(false);
  const [isShowReportingWells, setShowReportingWells] = useState(false);
  const [isShowLiveWells, setShowLiveWells] = useState(false);
  const [isPopinOpen, setPopinOpen] = useState(false);
  const [contriesList, setCountrieList] = useState<(string | undefined)[]>([]);
  const [isSelectedAllCheckBox, setSelectedAllCheckBox] = useState(false);

  const { categorizedWells } = useFetchAllCategorizedWells(true);

  const filterWells = useCallback(
    (wells: WellFullDetailsWithUniqueKeyDTO[], category: keyof CategorizedWells) => {
      if (
        category === 'nonActiveWells' &&
        (debouncedSearchInput?.length ?? 0) < MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH
      ) {
        return [];
      }

      return debouncedSearchInput
        ? wells.filter(well =>
            well.unifiedWellName.toLowerCase().includes(debouncedSearchInput.toLowerCase()),
          )
        : wells;
    },
    [debouncedSearchInput],
  );

  const onSearchInputChanged = ({ currentTarget: { value } }: ChangeEvent<HTMLInputElement>) =>
    setSearchInput(value);

  const getAllCountries = useMemo(
    () => [...new Set(categorizedWells.wellsByCountry.map(well => well.country))].filter(Boolean),
    [categorizedWells],
  );
  getAllCountries.sort();

  useEffect(() => {
    if (open) setPopinOpen(true);
  }, [open]);

  const onscroll = (e: React.UIEvent<HTMLElement>) => {
    if (isPopinOpen) {
      const bottom =
        e.currentTarget.offsetHeight + e.currentTarget.scrollTop >= e.currentTarget.scrollHeight;
      if (bottom && endPage !== selectedCategorisedWells.wellsByCountry.length - 1)
        setEndPage(endPage + 200);
    }
  };

  const onSelectCountries = (event: React.ChangeEvent<wellNameType>) => {
    const value = event.target.value as string[];
    if (value.filter(data => data === SELECT_ALL || data === UNSELECT_ALL).length > 0) {
      if (!isSelectedAllCheckBox) {
        setSelectedContries(getAllCountries as string[]);
      } else {
        setSelectedContries([] as string[]);
      }
      setSelectedAllCheckBox(!isSelectedAllCheckBox);
    } else {
      setSelectedContries(event.target.value as string[]);
    }
  };

  const onChangeLiveToggle = () => {
    setShowLiveWells(!isShowLiveWells);
  };

  const onChangeReportingToggle = () => {
    setShowReportingWells(!isShowReportingWells);
  };

  const resetCategorisedWellsToDefault = () => {
    setSelectedCategorisedWells(categorizedWells);
  };

  const resetContriesListToDefault = () => {
    setCountrieList(getAllCountries);
  };

  const onClickClearIcon = () => {
    setEndPage(20);
    resetCategorisedWellsToDefault();
    resetContriesListToDefault();
    setSelectedContries([]);
    setSelectedAllCheckBox(false);
  };

  const resetAdvancedSearchComponentsToDefault = () => {
    setSelectedContries([]);
    setShowLiveWells(false);
    setShowReportingWells(false);
    setSelectedAllCheckBox(false);
  };

  const onClickAdvancedSearch = () => {
    setAdvancedSearch(!isShowAdvancedSearch);
    setSearchInput('');
    setEndPage(20);
    setSearchInput('');
    if (isShowAdvancedSearch) {
      resetCategorisedWellsToDefault();
      resetContriesListToDefault();
    }
    resetAdvancedSearchComponentsToDefault();
  };

  const onPopinClose = useCallback(() => {
    setPopinOpen(false);
    setEndPage(20);
    setSearchInput('');
    onClose();
    setAdvancedSearch(false);
    setSelectedCategorisedWells(categorizedWells);
    setCountrieList(getAllCountries);
    resetAdvancedSearchComponentsToDefault();
  }, [onClose, categorizedWells, getAllCountries]);

  const getCountiesSelectElements = () => {
    const countriesList = [...getAllCountries];
    if (isSelectedAllCheckBox) {
      countriesList.unshift(UNSELECT_ALL);
    } else {
      countriesList.unshift(SELECT_ALL);
    }

    return countriesList;
  };

  /* This useEffect is used to set the countriesList on initial load */
  useEffect(() => {
    if (getAllCountries.length > 0) {
      setCountrieList(getAllCountries);
    }
  }, [getAllCountries]);

  /* This is useEffect is used to set the categorisedWells on initial load */
  useEffect(() => {
    if (categorizedWells) {
      setSelectedCategorisedWells(categorizedWells);
    }
  }, [categorizedWells]);

  /* This useEffect is used to set the categorisedWells based on advancedSearch inputs */
  useEffect(() => {
    if (isShowAdvancedSearch) {
      if (selectedCountries.length === 0 && !isShowLiveWells && !isShowReportingWells) {
        setSelectedCategorisedWells(categorizedWells);
        setCountrieList(getAllCountries);
      } else if (selectedCountries.length > 0) {
        const filteredWellByCountries: WellFullDetailsWithUniqueKeyDTO[] = [];
        const filteredActiveSensorWells: WellFullDetailsWithUniqueKeyDTO[] = [];
        const filteredActiveWellviewWells: WellFullDetailsWithUniqueKeyDTO[] = [];
        const countries: string[] = [];
        const formattedCategorisedWells = { ...categorizedWells };
        selectedCountries.forEach(country => {
          countries.push(country);

          formattedCategorisedWells.wellsByCountry.forEach(well => {
            if (well.country === country) {
              filteredWellByCountries.push(well);
            }
          });
        });
        if (isShowLiveWells) {
          selectedCountries.forEach(country => {
            formattedCategorisedWells.activeSensorWells.forEach(well => {
              if (well.country === country) {
                filteredActiveSensorWells.push(well);
              }
            });
          });
        }
        if (isShowReportingWells) {
          selectedCountries.forEach(country => {
            formattedCategorisedWells.activeWellviewWells.forEach(well => {
              if (well.country === country) {
                filteredActiveWellviewWells.push(well);
              }
            });
          });
        }
        formattedCategorisedWells.activeSensorWells = filteredActiveSensorWells;
        formattedCategorisedWells.activeWellviewWells = filteredActiveWellviewWells;
        formattedCategorisedWells.favouriteWells = [];
        formattedCategorisedWells.nonActiveWells = [];
        formattedCategorisedWells.wellsByCountry = filteredWellByCountries;
        setSelectedCategorisedWells(formattedCategorisedWells);
        setCountrieList(countries);
      } else if (isShowLiveWells || isShowReportingWells) {
        const formattedCategorisedWells = { ...categorizedWells };
        const formattedActiveSensorWells = formattedCategorisedWells.activeSensorWells;
        const formattedActiveWellviewWells = formattedCategorisedWells.activeWellviewWells;
        if (isShowLiveWells) {
          formattedCategorisedWells.activeSensorWells = formattedActiveSensorWells;
        } else {
          formattedCategorisedWells.activeSensorWells = [];
        }

        if (isShowReportingWells) {
          formattedCategorisedWells.activeWellviewWells = formattedActiveWellviewWells;
        } else {
          formattedCategorisedWells.activeWellviewWells = [];
        }
        formattedCategorisedWells.favouriteWells = [];
        formattedCategorisedWells.nonActiveWells = [];
        formattedCategorisedWells.wellsByCountry = [];
        setSelectedCategorisedWells(formattedCategorisedWells);
        setCountrieList([]);
      }
    }
  }, [
    selectedCountries,
    isShowLiveWells,
    isShowReportingWells,
    categorizedWells,
    getAllCountries,
    isShowAdvancedSearch,
  ]);

  return {
    onPopinClose,
    onSearchInputChanged,
    onClickAdvancedSearch,
    debouncedSearchInput,
    isShowAdvancedSearch,
    onSelectCountries,
    onChangeLiveToggle,
    onChangeReportingToggle,
    onClickClearIcon,
    getCountiesSelectElements,
    selectedCountries,
    isShowLiveWells,
    isShowReportingWells,
    isSelectedAllCheckBox,
    setSelectedAllCheckBox,
    selectedCategorisedWells,
    contriesList,
    filterWells,
    endPage,
    onscroll,
    setAdvancedSearch,
    setShowReportingWells,
    setShowLiveWells,
  };
};
